import React, { useState, useCallback, useMemo, MouseEvent } from 'react';
import { connect } from 'react-redux';
import { Popover, Avatar } from '@jjj/components';
import actions from '../../../redux/orgSwitcher/actions';
import authAction from '../../../redux/auth/actions';
import appAction from '../../../redux/app/actions';
import { t } from '../../../i18n';
import { isProduction } from '../../../settings';
import OrgList from './components/OrgList';
import SwitchOrgModal from './components/SwitchOrgModal';
import SettingsIcon from '../../../assets/icons/org-switcher/settings.svg';
import ProfileIcon from '../../../assets/icons/org-switcher/profile.svg';
import LogoutIcon from '../../../assets/icons/org-switcher/logout.svg';
import {
  OrgSwitcherWrapper,
  OrgsSection,
  Header,
  ActionsContainer,
  ActionItem,
  StyledLink,
  UserTitle,
  IconWrapper,
  UserAvatarWrapper,
} from './OrgSwitcher.style';
import { IUserOrgsData, RoleId } from '../../../redux/orgSwitcher/types';

const { logout } = authAction;
const { closeAll } = appAction;

const stopPropagation = (e: MouseEvent): void => {
  e.stopPropagation();
};

export interface ISwitchModalTarget {
  orgId: string;
  roleId: RoleId;
}

interface IProfile {
  name?: string;
  lastName?: string;
  avatar_url?: string;
  email?: string;
}

interface IOrgSwitcherProps {
  profile: IProfile | null;
  orgSwitcherData: IUserOrgsData | null;
  loading: boolean;
  fetchUserOrgs: () => void;
  logoutUser: () => void;
  closeAllModals: () => void;
}

const OrgSwitcher: React.FC<IOrgSwitcherProps> = ({
  profile,
  orgSwitcherData,
  loading,
  fetchUserOrgs,
  logoutUser,
  closeAllModals,
}) => {
  const [visible, setVisible] = useState(false);
  const [switchModalVisible, setSwitchModalVisible] = useState(false);
  const [switchModalTarget, setSwitchModalTarget] = useState<ISwitchModalTarget | null>(null);

  const userEmail = orgSwitcherData?.user.email || '';

  const currentOrgId = useMemo(() => {
    const orgs = orgSwitcherData?.organizations;

    if (!orgs?.length) return undefined;

    const activeOrg = orgs.find(o => o.active);

    return activeOrg?.id || orgs[0].id;
  }, [orgSwitcherData]);

  const handleVisibleChange = useCallback(
    (newVisible: boolean) => {
      setVisible(newVisible);

      if (newVisible) {
        fetchUserOrgs();
      }
    },
    [fetchUserOrgs],
  );

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  const handleLogout = useCallback(() => {
    logoutUser();
    closeAllModals();
    setVisible(false);
  }, [logoutUser, closeAllModals]);

  const handleShowSwitchModal = useCallback((orgId: string, roleId: RoleId) => {
    setVisible(false);
    setSwitchModalTarget({ orgId, roleId });
    setSwitchModalVisible(true);
  }, []);

  const handleHideSwitchModal = useCallback(() => {
    setSwitchModalVisible(false);
    setSwitchModalTarget(null);
  }, []);

  const profileInitials = useMemo(() => {
    if (profile?.name && profile?.lastName) {
      return `${profile.name[0]}${profile.lastName[0]}`;
    }

    return '';
  }, [profile?.name, profile?.lastName]);

  const hasOrganizations = orgSwitcherData && orgSwitcherData.organizations.length > 0;
  const showLoading = loading && !orgSwitcherData;

  const content = (
    <OrgSwitcherWrapper onClick={stopPropagation} data-cy="org-switcher-dropdown">
      {(hasOrganizations || showLoading) && (
        <OrgsSection>
          <Header>
            <h3>{t('dash.topbar.org_switcher.available_orgs')}</h3>
          </Header>

          <OrgList
            organizations={orgSwitcherData?.organizations || []}
            loading={showLoading}
            userEmail={userEmail}
            currentOrgId={currentOrgId}
            isOpen={visible}
            onShowSwitchModal={handleShowSwitchModal}
            onClose={handleClose}
          />
        </OrgsSection>
      )}

      <ActionsContainer>
        {!isProduction && (
          <StyledLink to="/settings" onClick={handleClose} data-cy="org-switcher-settings">
            <img alt="" src={SettingsIcon} />
            <span>{t('dash.topbar.settings_label')}</span>
          </StyledLink>
        )}

        <StyledLink to="/profile" onClick={handleClose} data-cy="org-switcher-profile">
          <img alt="" src={ProfileIcon} />
          <span>{t('dash.topbar.profile_label')}</span>
        </StyledLink>

        <ActionItem onClick={handleLogout} data-cy="org-switcher-logout">
          <img alt="" src={LogoutIcon} />
          <span>{t('dash.topbar.logout_label')}</span>
        </ActionItem>
      </ActionsContainer>
    </OrgSwitcherWrapper>
  );

  return (
    <div>
      <Popover
        content={content}
        trigger="click"
        open={visible}
        onOpenChange={handleVisibleChange}
        placement="bottomRight"
        popupAlign={{ offset: [-7, -7] }}
      >
        {profile && profile.name && (
          <UserTitle>
            <span className="user">{profile.name}</span>
            <IconWrapper>
              <i className="fas fa-chevron-down" />
            </IconWrapper>
          </UserTitle>
        )}
        <div className="isoImgWrapper">
          {profile && profile.avatar_url && profile.avatar_url !== 'nophoto' ? (
            <UserAvatarWrapper avatar={profile.avatar_url} />
          ) : (
            <Avatar>{profileInitials}</Avatar>
          )}
        </div>
      </Popover>

      <SwitchOrgModal
        visible={switchModalVisible}
        target={switchModalTarget}
        userEmail={userEmail}
        onClose={handleHideSwitchModal}
      />
    </div>
  );
};

interface IRootState {
  Auth: {
    profile: IProfile | null;
  };
  OrgSwitcher: {
    data: IUserOrgsData | null;
    loading: boolean;
  };
}

export default connect(
  (state: IRootState) => ({
    profile: state.Auth.profile,
    orgSwitcherData: state.OrgSwitcher.data,
    loading: state.OrgSwitcher.loading,
  }),
  dispatch => ({
    fetchUserOrgs: () => dispatch(actions.fetchUserOrgs()),
    logoutUser: () => dispatch(logout()),
    closeAllModals: () => dispatch(closeAll()),
  }),
)(OrgSwitcher);
